import secureApi from "../../utils/secureApi";
import { TournamentBulkResource } from "../../config/tournamentBulkTemplates";

export interface TournamentBulkUploadRequest {
  resource: TournamentBulkResource;
  tournamentId: string;
  tournamentSeasonId: string;
  sport: string;
  fileKey: string;
}

export interface BulkUploadIssue {
  label: string;
  type: "team" | "player" | "match";
}

export interface TournamentBulkUploadResult {
  successCount: number;
  issues: string[];
  unmatched: BulkUploadIssue[];
  raw: unknown;
}

export interface ParsedBulkUploadIssue {
  tab: string;
  row: string;
  message: string;
}

const unwrap = (response: any) => response?.data ?? response ?? {};

export async function bulkUploadTournamentResource({
  resource,
  tournamentId,
  tournamentSeasonId,
  sport,
  fileKey,
}: TournamentBulkUploadRequest): Promise<unknown> {
  if (!tournamentId || !tournamentSeasonId || !sport || !fileKey) {
    throw new Error("Tournament, season, sport, and upload file are required.");
  }

  const url =
    `/api/v2/tournaments/admin/${resource}/bulk-upload/` +
    `${encodeURIComponent(tournamentId)}/${encodeURIComponent(tournamentSeasonId)}` +
    `?sport=${encodeURIComponent(sport)}`;

  return secureApi.post(url, { fileKey });
}

const asStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((entry) =>
        typeof entry === "string"
          ? entry
          : entry?.message || entry?.error || JSON.stringify(entry)
      )
    : [];

const unmatched = (value: unknown, type: BulkUploadIssue["type"]): BulkUploadIssue[] =>
  asStrings(value).map((label) => ({ label, type }));

export function parseBulkUploadIssue(issue: string): ParsedBulkUploadIssue {
  const match = issue.match(
    /^Tab:\s*(.+?),\s*Row:\s*(.+?)\s*(?:→|->)\s*(.+)$/
  );

  return match
    ? { tab: match[1].trim(), row: match[2].trim(), message: match[3].trim() }
    : { tab: "", row: "", message: issue };
}

export function normalizeBulkUploadResponse(response: unknown): TournamentBulkUploadResult {
  const data = unwrap(response);
  const records =
    data?.fixtures ||
    data?.teams ||
    data?.players ||
    data?.playerStats ||
    data?.results ||
    data?.records ||
    [];
  const explicitSuccessCount = Number(data?.successCount ?? data?.uploaded);

  return {
    successCount: Number.isFinite(explicitSuccessCount)
      ? explicitSuccessCount
      : Array.isArray(records)
        ? records.length
        : 0,
    issues: [...asStrings(data?.errors), ...asStrings(data?.warnings)],
    unmatched: [
      ...unmatched(data?.unmatchedTeams, "team"),
      ...unmatched(data?.unmatchedPlayers, "player"),
      ...unmatched(data?.unmatchedMatches, "match"),
    ],
    raw: response,
  };
}
